import React, { useCallback, useEffect, useState } from "react";
import { BellPlus, CalendarClock, Clock, Loader2, Tag, Trash2, X } from "lucide-react";
import toast from "react-hot-toast";
import { getAppraisalReminders, deleteAppraisalReminder } from "../../../api/agent";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";

/**
 * The reminder SERIES on an appraisal.
 *
 * Simon Abbott, Monday 12985995388, 7 Sep 2026: "We also need to be able to add a
 * reminder series below the note inside appraisals." One call-back was never the
 * shape of the job — an appraisal that is not selling yet gets rung in a week,
 * then a month, then three — so this stacks as many as the agent wants and saves
 * them all with the appraisal.
 *
 * Nothing is written here: each pick is added to `value` and the parent posts the
 * lot as `appraisal_reminders` on save, so a cancelled modal books nothing. What
 * IS live is the "already booked" list, which reads the property's existing
 * series so the agent can see it and drop an entry.
 *
 * Simon Abbott, Monday 13166757620 (CRM-2026-00083), 30 Sep 2026: "Cant set a
 * reminder series in the holding area. Only notes." So the same picker now also
 * sits under the note box on a LEAD (LeadNotesModal), where there is no
 * property yet: the caller passes `fetchBooked` / `deleteBooked` and the booked
 * list reads and deletes through the lead's own reminders endpoint instead.
 * (Functions rather than a `leadId`, so this file does not import the lead API
 * and the appraisal screens' dependency graph is unchanged.)
 *
 * Props:
 *   propertyId    appraisal the series belongs to (omit on a not-yet-saved one)
 *   fetchBooked   optional async () => axios response, replaces the appraisal GET
 *   deleteBooked  optional async (kind, id), replaces the appraisal DELETE
 *   value         pending entries [{ key, label, date: Date, reason }]
 *   onChange      called with the next pending list
 *   label         heading (default "Reminder series")
 *   pendingLabel  heading over the staged entries (default "Saving with this appraisal")
 */

// Must match ContactReminder.REASON_CHOICES on the backend.
const REASONS = [
    { key: "contact", label: "Contact" },
    { key: "follow_up", label: "Follow Up" },
    { key: "appointment", label: "Appointment" },
    { key: "action_required", label: "Action Required" },
    { key: "review", label: "Review" },
];

const PRESETS = [
    { key: "1d", label: "1 day", add: (d) => d.setDate(d.getDate() + 1) },
    { key: "1w", label: "1 week", add: (d) => d.setDate(d.getDate() + 7) },
    { key: "2w", label: "2 weeks", add: (d) => d.setDate(d.getDate() + 14) },
    { key: "1m", label: "1 month", add: (d) => d.setMonth(d.getMonth() + 1) },
    { key: "3m", label: "3 months", add: (d) => d.setMonth(d.getMonth() + 3) },
    { key: "6m", label: "6 months", add: (d) => d.setMonth(d.getMonth() + 6) },
    { key: "12m", label: "12 months", add: (d) => d.setMonth(d.getMonth() + 12) },
];

// Apply the preset's offset, then pin to the chosen call-back time of day so the
// due date does not drift with the hour the agent happened to tap it.
const presetDueDate = (mutate, hours, minutes) => {
    const d = new Date();
    mutate(d);
    d.setHours(hours, minutes, 0, 0);
    return d;
};

// Deliberately viewer-local, exactly as ReminderPresets does it: this echoes back
// a date the agent has just picked with their own clock (the presets call
// setHours locally), so re-casting it to Melbourne would show them a time they
// did not choose. What is stored is a UTC instant, and every place that reads it
// back — the booked list above, Who-to-Call, Tasks — renders it in Melbourne.
// eslint-disable-next-line no-restricted-syntax
const formatDue = (d) => d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

const AppraisalReminderSeries = ({
    propertyId,
    fetchBooked,
    deleteBooked,
    value = [],
    onChange,
    label = "Reminder series",
    pendingLabel = "Saving with this appraisal",
}) => {
    const [time, setTime] = useState("09:00");
    const [reason, setReason] = useState("follow_up");
    const [showCustom, setShowCustom] = useState(false);
    const [customDate, setCustomDate] = useState("");
    const [booked, setBooked] = useState([]);
    const [loadingBooked, setLoadingBooked] = useState(false);
    const [removing, setRemoving] = useState(null);

    const loadBooked = useCallback(async () => {
        if (!propertyId && !fetchBooked) {
            setBooked([]);
            return;
        }
        try {
            setLoadingBooked(true);
            const response = fetchBooked
                ? await fetchBooked()
                : await getAppraisalReminders(propertyId);
            const rows = response?.data?.results ?? [];
            setBooked(Array.isArray(rows) ? rows : []);
        } catch (error) {
            // A series that will not load must not block writing the note or
            // adding new reminders — just show nothing.
            console.error("Could not load the appraisal reminder series:", error);
            setBooked([]);
        } finally {
            setLoadingBooked(false);
        }
    }, [propertyId, fetchBooked]);

    useEffect(() => {
        loadBooked();
    }, [loadBooked]);

    const parseTime = () => {
        const [h, m] = String(time).split(":").map(Number);
        return {
            hours: Number.isFinite(h) ? h : 9,
            minutes: Number.isFinite(m) ? m : 0,
        };
    };

    const addEntry = (date, presetLabel) => {
        // Same date AND same reason twice is a double-booking, not a series.
        const clash = value.some(
            (entry) => entry.reason === reason && entry.date.getTime() === date.getTime()
        );
        if (clash) {
            toast.error("That reminder is already in the series.");
            return;
        }
        onChange?.([
            ...value,
            { key: `${date.getTime()}-${reason}`, label: presetLabel, date, reason },
        ]);
    };

    const handlePreset = (preset) => {
        const { hours, minutes } = parseTime();
        addEntry(presetDueDate(preset.add, hours, minutes), preset.label);
    };

    const handleCustom = () => {
        if (!customDate) return;
        const date = new Date(`${customDate}T${time}:00`);
        if (Number.isNaN(date.getTime())) {
            toast.error("Pick a valid date and time");
            return;
        }
        addEntry(date, formatDue(date));
        setCustomDate("");
        setShowCustom(false);
    };

    const removeEntry = (key) => onChange?.(value.filter((entry) => entry.key !== key));

    const removeBooked = async (row) => {
        try {
            setRemoving(`${row.kind}-${row.id}`);
            if (deleteBooked) {
                await deleteBooked(row.kind, row.id);
            } else {
                await deleteAppraisalReminder(propertyId, row.kind, row.id);
            }
            setBooked((prev) => prev.filter((r) => !(r.kind === row.kind && r.id === row.id)));
            toast.success("Reminder removed");
        } catch (error) {
            console.error("Could not remove the reminder:", error);
            toast.error("Failed to remove the reminder");
        } finally {
            setRemoving(null);
        }
    };

    const reasonLabel = (key) => REASONS.find((r) => r.key === key)?.label || "Follow Up";

    return (
        <div className="rounded-xl bg-white border border-gray-100 px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-gray-500">
                    <BellPlus className="h-4 w-4 text-teal-500" />
                    {label}
                </div>
                <label className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600">
                    <Clock className="h-4 w-4 text-teal-500" />
                    at
                    <input
                        type="time"
                        aria-label="Call-back time"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        className="rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
                    />
                </label>
            </div>

            {/* Already booked, so the agent adds to the series instead of
                repeating it. Reminders set before this list existed have no
                property on them and stay on the owner in Who-to-Call. */}
            {loadingBooked ? (
                <div className="flex items-center gap-1.5 text-[12px] text-gray-400 mb-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading reminders...
                </div>
            ) : (
                booked.length > 0 && (
                    <div className="mb-2 space-y-1">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
                            Already booked
                        </p>
                        {booked.map((row) => (
                            <div
                                key={`${row.kind}-${row.id}`}
                                className="flex items-center gap-2 rounded-lg bg-teal-50/70 border border-teal-100 px-2.5 py-1.5"
                            >
                                <CalendarClock className="h-3.5 w-3.5 text-teal-500 shrink-0" />
                                <span className="text-[12px] font-semibold text-gray-700">
                                    {row.due_date ? formatMelbourneDateTime(row.due_date) : "No date"}
                                </span>
                                {row.reason_label && (
                                    <span className="text-[11px] text-gray-500">{row.reason_label}</span>
                                )}
                                <button
                                    type="button"
                                    aria-label="Remove this reminder"
                                    title="Remove this reminder"
                                    onClick={() => removeBooked(row)}
                                    disabled={removing === `${row.kind}-${row.id}`}
                                    className="ml-auto p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                                >
                                    {removing === `${row.kind}-${row.id}` ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <Trash2 className="h-3.5 w-3.5" />
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                )
            )}

            <div className="mb-2">
                <div className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                    <Tag className="h-4 w-4 text-teal-500" />
                    Reason
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {REASONS.map((r) => (
                        <button
                            key={r.key}
                            type="button"
                            onClick={() => setReason(r.key)}
                            className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                                reason === r.key
                                    ? "border-teal-400 bg-teal-500 text-white"
                                    : "border-gray-200 bg-white text-gray-700 hover:border-teal-300 hover:text-teal-700"
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-wrap gap-1.5">
                {PRESETS.map((p) => (
                    <button
                        key={p.key}
                        type="button"
                        onClick={() => handlePreset(p)}
                        className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-teal-300 hover:text-teal-700"
                    >
                        {p.label}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={() => setShowCustom((s) => !s)}
                    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                        showCustom
                            ? "border-teal-300 bg-teal-50 text-teal-700"
                            : "border-gray-200 bg-white text-gray-700 hover:border-teal-300 hover:text-teal-700"
                    }`}
                >
                    <CalendarClock className="h-3.5 w-3.5" />
                    Custom
                </button>
            </div>

            {showCustom && (
                <div className="mt-2 flex items-center gap-2">
                    <input
                        type="date"
                        aria-label="Custom reminder date"
                        value={customDate}
                        onChange={(e) => setCustomDate(e.target.value)}
                        min={new Date().toISOString().slice(0, 10)}
                        className="rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
                    />
                    <button
                        type="button"
                        onClick={handleCustom}
                        disabled={!customDate}
                        className="inline-flex items-center gap-1 rounded-lg bg-teal-500 px-3 py-1.5 text-sm font-bold text-white hover:bg-teal-600 disabled:opacity-50"
                    >
                        Add
                    </button>
                </div>
            )}

            {value.length > 0 && (
                <div className="mt-2.5 space-y-1">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
                        {pendingLabel}
                    </p>
                    {value.map((entry) => (
                        <div
                            key={entry.key}
                            className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-2.5 py-1.5"
                        >
                            <BellPlus className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                            <span className="text-[12px] font-semibold text-emerald-800">
                                {formatDue(entry.date)}
                            </span>
                            <span className="text-[11px] text-emerald-600">
                                {reasonLabel(entry.reason)}
                            </span>
                            <button
                                type="button"
                                aria-label={`Remove the ${entry.label} reminder`}
                                title="Remove"
                                onClick={() => removeEntry(entry.key)}
                                className="ml-auto p-1 rounded text-emerald-500 hover:text-red-500 hover:bg-red-50 transition-colors"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AppraisalReminderSeries;
